import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../services/axiosInstance";
import GlobalTable from "../components/GlobalTable";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Accordion, AccordionTab } from "primereact/accordion";
// import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { BrowserMultiFormatReader } from '@zxing/browser';
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { InputText } from "primereact/inputtext";
import { FileUpload } from "primereact/fileupload";

const InventoryBulk = () => {
    const [dialogVisible, setDialogVisible] = useState(false);
    const [inventory, setInventory] = useState({ product: "", quantity: "", product_name: "" });
    const [inventories, setInventories] = useState([]);
    const [scannerVisible, setScannerVisible] = useState(false);
    const fileUploadRef = useRef(null);
    const quantityInputRef = useRef(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Fetch inventory list on load
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get("/api/inventory/");
                setInventories(res.data);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleQuantitySubmit = async (e) => {
        e.preventDefault();
        try {
            const result = await axiosInstance.post("/api/inventory/", inventory);
            setInventories((prev) => [...prev, result.data]);
            handleApiSuccess("Inventory updated successfully!");
            setDialogVisible(false);
            setScannerVisible(true);
            setInventory({ product: "", quantity: "", product_name: "" });
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleCameraScan = async (barcode) => {
        try {
            const res = await axiosInstance.get(`/api/products?barcode=${barcode}`);
            if (res.data) {
                setInventory({ product: res.data.id, product_name: res.data.name, quantity: "" });
                setDialogVisible(true);
                setScannerVisible(false); // Pause camera when popup opens
            } else {
                handleApiError({Detail: "Product not found for the scanned barcode." });
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleFileUpload = async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
            await axiosInstance.post("/api/inventory/import/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            handleApiSuccess("File imported succesfully");
            fileUploadRef.current.clear();
        } catch (error) {
            handleApiError(error);
            fileUploadRef.current.clear();
        }
    };

    useEffect(() => {
        if (dialogVisible) {
            setTimeout(() => {
                quantityInputRef.current?.focus();
            }, 300);
        }
    }, [dialogVisible]);

    const codeReaderRef = useRef(null);
    let controls = null;
    
    useEffect(() => {
    if (scannerVisible) {
            const reader = new BrowserMultiFormatReader();
            codeReaderRef.current = reader;

            reader.decodeFromVideoDevice(
                null,
                "barcode-video",
                (result, error, ctrl) => {
                    if (result) {
                        handleCameraScan(result.getText());
                        ctrl.stop();             
                        controls = null;
                        setScannerVisible(false); 
                    }
                    if (!controls) {
                        controls = ctrl;          
                    }
                }
            ).catch((err) => {
                console.error("Camera start error:", err);
            });
        }

        return () => {
            if (controls) {
                controls.stop(); 
            }
        };
    }, [scannerVisible]);


        return (
        <div>
            <div className="containers">
            <h4 className="page-title">Bulk Inventory Management</h4>
            
            <Accordion>
                <AccordionTab header="Using Camera for Barcode Scanning">
                    {scannerVisible ? (
                        <>
                            <video id="barcode-video" width="100%" height="400px" />
                            <Button
                                label="Stop Camera"
                                onClick={() => {
                                    if (controls) {
                                        controls.stop(); 
                                    }
                                    setScannerVisible(false);
                                }}
                                className="p-button-danger"
                            />
                        </>
                    ) : (
                        <Button
                            label="Start Scanning"
                            onClick={() => {
                                setScannerVisible(true);
                            }}
                            className="p-button-success"
                        />
                    )}

                </AccordionTab>

                <AccordionTab header="Using Barcode Reader">
                    <div>
                        <label htmlFor="product">Scan Product: </label>
                    </div>
                    <div className="col-5">
                        <InputText
                            type="text"
                            placeholder="Enter or scan barcode"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleCameraScan(e.target.value);
                                    e.target.value = ""; // Clear the input field
                                }
                            }}
                        />
                    </div>
                </AccordionTab>

                <AccordionTab header="Import Inventory">
                    <FileUpload
                        name="file"
                        ref={fileUploadRef} 
                        accept=".csv,.xlsx,.xls"
                        mode="advanced"
                        customUpload
                        uploadHandler={(e) => handleFileUpload(e.files[0])}
                        auto={false}
                        chooseLabel="Choose File"
                        uploadLabel="Upload"
                        dragDropInstructions="Drag and drop a file here or click to browse"
                        emptyTemplate={<p className="p-m-0">Drag and drop a file here to upload.</p>}
                    />
                </AccordionTab>
            </Accordion>

            <Dialog
                header="Enter Quantity"
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
            >
                <form onSubmit={handleQuantitySubmit}>
                    <div className="field">
                        <label>{inventory.product_name}</label>
                    </div>
                    <div className="col-5">
                        <label htmlFor="quantity">Quantity</label>
                        <InputNumber
                            ref={quantityInputRef}
                            id="quantity"
                            value={inventory.quantity}
                            onValueChange={(e) => setInventory({ ...inventory, quantity: e.value })}
                        />
                    </div>
                    <div className="dialog-footer"
                            style={{
                                    display: "flex",
                                    gap: "1rem",
                                    justifyContent: "right",
                                    flexWrap: "nowrap",
                                    marginTop: "1rem",
                                }}
                    >
                        <Button
                            label="Save"
                            type="submit"
                            className="p-button-primary"
                        />
                        <Button
                            label="Cancel"
                            className="p-button-secondary"
                            onClick={(e) => {
                                setDialogVisible(false);
                                e.preventDefault();
                                setScannerVisible(true);
                            }}
                        />
                    </div>
                </form>
            </Dialog>
            </div>
            <div className="containers">
            <GlobalTable
                columns={[
                    { Header: "Product", accessor: "product_name" },
                    { Header: "Quantity", accessor: "quantity" },
                    { Header: "Reorder Level", accessor: "reorder_level" },
                ]}
                data={inventories}
                tabletitle="Inventory List"
                showEditColumn= {false}
                loading={loading}
            />
            </div>
        </div>
    );
};

export default InventoryBulk;
